import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Modal, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InfoCircle, Trash } from 'iconsax-react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports as sportsList, practicedSportsRoles } from '../../../../components/Util/enum';
import { useProfile } from '../../../../../hooks/useProfile';

const emptyForm = {
    nome: '', cognome: '', luogoNascita: '', birthdate: '', codiceFiscale: '',
    indirizzo: '', cap: '', citta: '', provincia: '', email: '', telefono: '',
};

const Req = () => <span className="text-danger"> *</span>;

const emptySportRow = { sportType: '', sportRole: '' };

const AggiungiPlayer = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { profile } = useProfile({ autoFetch: true });
    // Se arrivo da una vendita manuale, torno lì col cliente creato selezionato.
    const returnTo = searchParams.get('returnTo');
    const [createdCustomer, setCreatedCustomer] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalType, setModalType] = useState('success');
    // Sport praticati: si scelgono tra quelli della struttura, così il player
    // nasce con practicedSportTypes valorizzato come se si fosse registrato da solo.
    const [sportRows, setSportRows] = useState([emptySportRow]);

    // Sport della struttura, con l'etichetta in italiano.
    const clubSports = useMemo(() => {
        const configured = (profile?.club?.sports || []).map((s) => s.sportType);
        return sportsList.filter((s) => configured.includes(s.value));
    }, [profile]);

    // Con un solo sport a catalogo la scelta è obbligata: la preselezioniamo.
    useEffect(() => {
        if (clubSports.length !== 1) return;
        setSportRows((prev) => (prev.length === 1 && !prev[0].sportType
            ? [{ sportType: clubSports[0].value, sportRole: '' }]
            : prev));
    }, [clubSports]);

    const rolesFor = (sportType) => practicedSportsRoles[sportType] || [];

    const updateSportRow = (index, sportType, sportRole) => {
        setSportRows((prev) => prev.map((row, i) => (i === index ? { sportType, sportRole } : row)));
        if (sportType) {
            setErrors((prev) => {
                const n = { ...prev };
                delete n.sports;
                return n;
            });
        }
    };

    const addSportRow = () => setSportRows((prev) => [...prev, emptySportRow]);

    const removeSportRow = (index) => {
        setSportRows((prev) => (prev.length === 1 ? [emptySportRow] : prev.filter((_, i) => i !== index)));
    };

    // Ogni sport una volta sola: nelle altre righe non si ripropone.
    const sportOptionsFor = (index) => {
        const taken = sportRows.filter((_, i) => i !== index).map((row) => row.sportType);
        return clubSports.filter((s) => !taken.includes(s.value));
    };

    const selectedSports = () => sportRows
        .filter((row) => row.sportType)
        .map((row) => (row.sportRole ? { sportType: row.sportType, sportRole: row.sportRole } : { sportType: row.sportType }));

    const update = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const n = { ...prev };
                delete n[field];
                return n;
            });
        }
    };

    const validate = () => {
        const e = {};
        const req = (f, msg) => { if (!form[f] || !String(form[f]).trim()) e[f] = msg; };

        req('nome', 'Il nome è obbligatorio');
        req('cognome', 'Il cognome è obbligatorio');
        req('luogoNascita', 'Il luogo di nascita è obbligatorio');
        req('birthdate', 'La data di nascita è obbligatoria');
        req('codiceFiscale', 'Il codice fiscale è obbligatorio');
        req('indirizzo', "L'indirizzo è obbligatorio");
        req('cap', 'Il CAP è obbligatorio');
        req('citta', 'La città è obbligatoria');
        req('provincia', 'La provincia è obbligatoria');
        req('email', "L'email è obbligatoria");
        req('telefono', 'Il telefono è obbligatorio');

        if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            e.email = 'Formato email non valido';
        }
        if (form.codiceFiscale.trim() && !/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(form.codiceFiscale.trim())) {
            e.codiceFiscale = 'Controlla il formato del codice fiscale (16 caratteri, es. RSSMRA85M01H501Z)';
        }
        if (form.cap.trim() && !/^\d{5}$/.test(form.cap.trim())) {
            e.cap = 'Il CAP deve avere 5 cifre';
        }
        if (form.provincia.trim() && !/^[A-Za-z]{2}$/.test(form.provincia.trim())) {
            e.provincia = 'Sigla provincia di 2 lettere (es. AV)';
        }
        // Lo sport è obbligatorio: senza, il player nascerebbe con
        // practicedSportTypes vuoto. Se la struttura non ha sport a catalogo non
        // c'è nulla da scegliere e non si blocca la creazione.
        if (clubSports.length > 0 && selectedSports().length === 0) {
            e.sports = 'Seleziona almeno uno sport praticato';
        }
        if (form.birthdate) {
            const d = new Date(form.birthdate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (isNaN(d.getTime())) e.birthdate = 'Data non valida';
            else if (d > today) e.birthdate = 'La data non può essere nel futuro';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setLoading(true);
        try {
            const apiClient = new MyApiClient();
            const tokenDetailsString = sessionStorage.getItem('userDetails');
            if (!tokenDetailsString) {
                setModalMessage('Sessione non valida. Effettua di nuovo il login.');
                setModalType('error');
                setShowModal(true);
                return;
            }
            const token = JSON.parse(tokenDetailsString).idToken;

            const payload = {
                givenName: form.nome.trim(),
                familyName: form.cognome.trim(),
                email: form.email.trim().toLowerCase(),
                phoneNumber: form.telefono.trim(),
                birthdate: form.birthdate,
                birthPlace: form.luogoNascita.trim(),
                taxCode: form.codiceFiscale.trim().toUpperCase(),
                address: {
                    streetAddress: form.indirizzo.trim(),
                    locality: form.citta.trim(),
                    postalCode: form.cap.trim(),
                    province: form.provincia.trim().toUpperCase(),
                    country: 'IT',
                },
                sports: selectedSports(),
            };

            const response = await apiClient.createTemporaryPlayer(token, payload);

            if (response && response.error) {
                const detail = (response.error && (response.error.detail || response.error)) || 'Errore durante il salvataggio del player.';
                setModalMessage(typeof detail === 'string' ? detail : 'Errore durante il salvataggio del player.');
                setModalType('error');
                setShowModal(true);
                return;
            }

            const newUserId = response?.data?.userId;
            setCreatedCustomer(newUserId ? { id: newUserId, name: `${form.nome.trim()} ${form.cognome.trim()}` } : null);
            setModalMessage('Player salvato con successo.');
            setModalType('success');
            setShowModal(true);
            setForm(emptyForm);
            setSportRows([emptySportRow]);
        } catch (error) {
            setModalMessage('Errore durante il salvataggio del player. Riprova.');
            setModalType('error');
            setShowModal(true);
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        if (modalType !== 'success') return;
        // Ritorno alla vendita manuale col cliente appena creato selezionato.
        if (returnTo && createdCustomer) {
            const sep = returnTo.includes('?') ? '&' : '?';
            navigate(`${returnTo}${sep}newCustomerId=${encodeURIComponent(createdCustomer.id)}&newCustomerName=${encodeURIComponent(createdCustomer.name)}`);
        } else {
            navigate('/struttura/clienti/list');
        }
    };

    const field = (name, label, { type = 'text', placeholder = '', maxLength } = {}) => (
        <Form.Group className="mb-3">
            <Form.Label>{label}<Req /></Form.Label>
            <Form.Control
                type={type}
                value={form[name]}
                placeholder={placeholder}
                maxLength={maxLength}
                onChange={(e) => update(name, e.target.value)}
                isInvalid={!!errors[name]}
            />
            <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
        </Form.Group>
    );

    return (
        <>
            <div className="sid-info-banner">
                <span className="sid-info-banner__icon"><InfoCircle size={22} variant="Bulk" /></span>
                <div className="sid-info-banner__body">
                    <div className="sid-info-banner__title">Dati essenziali per l'inserimento di un Player</div>
                    <p>I Player potranno modificare i loro dati tramite l'applicazione Sportid Player.</p>
                </div>
            </div>

            <Form onSubmit={(e) => e.preventDefault()}>
                <Row>
                    <Col lg={6}>
                        <Card>
                            <Card.Header><Card.Title>Dati anagrafici</Card.Title></Card.Header>
                            <Card.Body>
                                {field('nome', 'Nome')}
                                {field('cognome', 'Cognome')}
                                {field('luogoNascita', 'Luogo di nascita')}
                                {field('birthdate', 'Data di nascita', { type: 'date' })}
                                {field('codiceFiscale', 'Codice fiscale', { maxLength: 16 })}
                                {field('indirizzo', 'Indirizzo')}
                                {field('cap', 'CAP', { maxLength: 5 })}
                                {field('citta', 'Città')}
                                {field('provincia', 'Provincia', { maxLength: 2, placeholder: 'es. AV' })}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={6}>
                        <Card>
                            <Card.Header><Card.Title>Contatti</Card.Title></Card.Header>
                            <Card.Body>
                                {field('email', 'Email', { type: 'email' })}
                                {field('telefono', 'Telefono cellulare', { type: 'tel' })}
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Header><Card.Title>Sport praticati</Card.Title></Card.Header>
                            <Card.Body>
                                {clubSports.length === 0 ? (
                                    <p className="mb-0 text-muted">
                                        La struttura non ha ancora configurato nessuno sport: puoi aggiungerli
                                        dalla sezione &laquo;La mia struttura&raquo;.
                                    </p>
                                ) : (
                                    <>
                                        <p className="text-muted">
                                            Scegli gli sport che il player pratica nella tua struttura. Dove previsto
                                            puoi indicare anche il ruolo.
                                        </p>
                                        {sportRows.map((row, index) => (
                                            <Row key={index} className="align-items-end mb-3">
                                                <Col sm={rolesFor(row.sportType).length > 0 ? 6 : 11}>
                                                    <Form.Group>
                                                        <Form.Label>Sport{index === 0 && <Req />}</Form.Label>
                                                        <Form.Select
                                                            value={row.sportType}
                                                            onChange={(e) => updateSportRow(index, e.target.value, '')}
                                                        >
                                                            <option value="">Seleziona uno sport</option>
                                                            {sportOptionsFor(index).map((s) => (
                                                                <option key={s.value} value={s.value}>{s.label}</option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                                {rolesFor(row.sportType).length > 0 && (
                                                    <Col sm={5}>
                                                        <Form.Group>
                                                            <Form.Label>Ruolo</Form.Label>
                                                            <Form.Select
                                                                value={row.sportRole}
                                                                onChange={(e) => updateSportRow(index, row.sportType, e.target.value)}
                                                            >
                                                                <option value="">Nessun ruolo</option>
                                                                {rolesFor(row.sportType).map((r) => (
                                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                )}
                                                <Col sm={1} className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-link text-danger p-0 mb-2"
                                                        title="Rimuovi lo sport"
                                                        onClick={() => removeSportRow(index)}
                                                    >
                                                        <Trash size={20} />
                                                    </button>
                                                </Col>
                                            </Row>
                                        ))}
                                        {errors.sports && (
                                            <div className="text-danger small mb-2">{errors.sports}</div>
                                        )}
                                        <button
                                            type="button"
                                            className="btn sid-btn-ghost btn-sm"
                                            onClick={addSportRow}
                                            disabled={sportRows.length >= clubSports.length}
                                        >
                                            Aggiungi un altro sport
                                        </button>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Form>

            <div className="sid-form-actions d-flex justify-content-end gap-2 mt-1 mb-4">
                <button type="button" className="btn sid-btn-ghost" onClick={() => navigate(returnTo || '/struttura/clienti/list')}>
                    Annulla
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? 'Salvataggio…' : 'Salva Player'}
                </button>
            </div>

            <Modal show={showModal} onHide={closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalType === 'success' ? 'Successo' : 'Errore'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalMessage}</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-primary" onClick={closeModal}>Chiudi</button>
                </Modal.Footer>
            </Modal>

            <Modal show={loading} centered backdrop="static">
                <Modal.Body className="text-center">
                    <Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner>
                    <div className="mt-2">Salvataggio in corso…</div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AggiungiPlayer;
